import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Copy, Search } from "lucide-react";
import { exportCSV } from "@/lib/exports";
import { ExportBtn } from "@/components/dashboard/ExportBtn";

const BUCKET_COLORS = { "0-30": "#10B981", "31-60": "#F59E0B", "61-90": "#EA580C", "90+": "#F43F5E" };
const ageColor = (d) => (d <= 30 ? "text-emerald-400" : d <= 60 ? "text-amber-400" : d <= 90 ? "text-orange-500" : "text-rose-500");

export const InventoryFifo = ({ aging, fifo }) => {
  const [expanded, setExpanded] = useState(null);
  const [oldQ, setOldQ] = useState("");
  if (!aging || !fifo) return <div className="h-96 bg-[#1E293B] border border-[#334155] rounded-lg animate-pulse" />;

  const copyChassis = (c) => { navigator.clipboard.writeText(c); toast.success(`Chassis ${c} copied`); };
  const oldRows = aging.old_stock.filter((r) => !oldQ || `${r.chassis_no} ${r.model_name} ${r.model_code} ${r.colour}`.toLowerCase().includes(oldQ.toLowerCase()));
  const totalStock = aging.buckets.reduce((t, b) => t + b.count, 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div data-testid="aging-chart" className="bg-[#1E293B] border border-[#334155] rounded-lg p-5">
          <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Stock Aging Distribution</h3>
          <p className="text-xs text-slate-400 mb-4">{totalStock} chassis in own stock, by days since purchase.</p>
          <div className="space-y-4">
            {aging.buckets.map((b) => {
              const pct = totalStock ? Math.round((b.count / totalStock) * 100) : 0;
              return (
                <div key={b.bucket} data-testid={`aging-bucket-${b.bucket}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">{b.bucket} days</span>
                    <span className="font-mono font-bold" style={{ color: BUCKET_COLORS[b.bucket] }}>{b.count} <span className="text-slate-500 font-normal text-xs">({pct}%)</span></span>
                  </div>
                  <div className="h-3 rounded-full bg-[#0B0F17] overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: BUCKET_COLORS[b.bucket] }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div data-testid="fifo-panel" className="lg:col-span-2 bg-[#1E293B] border border-orange-600/40 rounded-lg p-5">
          <h3 className="text-xl font-bold mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>FIFO Billing Suggestions — Sell These First</h3>
          <p className="text-xs text-slate-400 mb-4">One card per model: the OLDEST chassis in stock. Click a card for the full FIFO queue. Copy the chassis and bill it.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[560px] overflow-auto pr-1">
            {fifo.map((s, i) => (
              <div key={s.model_name} data-testid={`fifo-suggestion-card-${i}`}
                className="rounded-lg border border-[#334155] bg-[#111827] p-4 transition-all duration-200 hover:border-orange-500/60 cursor-pointer"
                onClick={() => setExpanded(expanded === i ? null : i)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-slate-100 text-base truncate">{s.model_name} <span className="text-xs text-slate-500 font-mono">({s.model_code})</span></p>
                    <p className="font-mono text-sm text-orange-400 flex items-center gap-1.5 mt-1">
                      {s.chassis_no}
                      <button data-testid={`copy-chassis-btn-${i}`} onClick={(e) => { e.stopPropagation(); copyChassis(s.chassis_no); }}
                        className="text-slate-500 hover:text-white transition-colors"><Copy className="h-3.5 w-3.5" /></button>
                    </p>
                    <p className="text-xs text-slate-400 mt-1">{s.colour} · purchased {s.inward_date}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`text-3xl font-extrabold font-mono ${ageColor(s.age_days)}`}>{s.age_days}<span className="text-sm">d</span></span>
                    <Badge variant="outline" className="block mt-1 border-slate-600 text-slate-400 text-[10px]">{s.stock_count} in stock</Badge>
                  </div>
                </div>
                {expanded === i && s.queue.length > 1 && (
                  <div className="mt-3 pt-3 border-t border-slate-800 space-y-1.5">
                    <p className="text-[10px] uppercase tracking-widest text-slate-500">FIFO Queue</p>
                    {s.queue.map((q, qi) => (
                      <div key={q.chassis_no} className="flex justify-between text-xs font-mono">
                        <span className={qi === 0 ? "text-orange-400" : "text-slate-400"}>{qi + 1}. {q.chassis_no} <span className="text-slate-600">({q.colour})</span></span>
                        <span className={ageColor(q.age_days)}>{q.age_days}d</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {fifo.length === 0 && <p className="text-sm text-slate-500">No in-stock chassis found.</p>}
          </div>
        </div>
      </div>
      <div data-testid="old-stock-table" className="bg-[#1E293B] border border-rose-600/40 rounded-lg p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <h3 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Old Stock Alerts <span className="text-sm text-slate-400 font-normal">({oldRows.length} chassis · 60+ days · oldest first)</span></h3>
          <ExportBtn onCSV={() => exportCSV(aging.old_stock.map(({ pricing, ...r }) => r), "old-stock-alerts")} />
        </div>
        <p className="text-xs text-slate-400 mb-3">FIFO order — the top row is the single oldest vehicle in your showroom. Sell top to bottom.</p>
        <div className="relative mb-3">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
          <Input data-testid="old-stock-search" placeholder="Search chassis / model / code / colour…" value={oldQ} onChange={(e) => setOldQ(e.target.value)}
            className="pl-8 h-9 max-w-md bg-[#0B0F17] border-[#334155] text-slate-100 placeholder:text-slate-500" />
        </div>
        <div className="max-h-[560px] overflow-auto">
          {oldRows.length === 0 && <p className="text-sm text-slate-500">No old stock matches. 🎉</p>}
          <table className="w-full min-w-[640px] text-sm">
            <thead className="sticky top-0 bg-[#1E293B]">
              <tr className="text-left text-xs uppercase tracking-widest text-slate-400 border-b border-[#334155]">
                <th className="pb-2 pr-3">Priority</th><th className="pb-2 pr-3">Chassis</th><th className="pb-2 pr-3">Model (Code)</th>
                <th className="pb-2 pr-3">Colour</th><th className="pb-2 pr-3">Purchased</th><th className="pb-2 pr-3 text-right">Age</th><th className="pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {oldRows.map((r, i) => (
                <tr key={r.chassis_no || i} data-testid={`old-stock-row-${i}`} className="border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors">
                  <td className="py-3 pr-3">
                    <Badge className={`font-mono text-white ${i < 3 ? "bg-rose-600" : i < 10 ? "bg-orange-600" : "bg-slate-700"}`}>#{i + 1}</Badge>
                  </td>
                  <td className="py-3 pr-3 font-mono text-base text-orange-400">{r.chassis_no}</td>
                  <td className="py-3 pr-3 text-slate-200 text-base">{r.model_name} <span className="text-slate-500 font-mono text-xs">({r.model_code})</span></td>
                  <td className="py-3 pr-3 text-slate-400">{r.colour}</td>
                  <td className="py-3 pr-3 font-mono text-slate-400">{r.inward_date}</td>
                  <td className={`py-3 pr-3 text-right font-mono font-extrabold text-xl ${ageColor(r.age_days)}`}>{r.age_days}d</td>
                  <td className="py-3 text-right">
                    <button data-testid={`old-stock-copy-${i}`} onClick={() => copyChassis(r.chassis_no)} className="text-slate-500 hover:text-white transition-colors"><Copy className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
